import React, { useState, useEffect, useCallback } from 'react';
import Header from './Header';
import Footer from './Footer';
import useApiRequest from './useApiRequest';
import { domain } from './Domain';
import { useSessionId } from './SessionContext';
import useFetch from './useFetch';
import '../css/Cart.css';
import { motion } from 'framer-motion';
import InquiryForm from './InquiryForm';
import { Link } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { useNavigate } from 'react-router-dom';
import { DNA } from 'react-loader-spinner';

const FIELD_LABELS = {
    company: 'Company name',
    location: 'Address',
    city: 'City',
    tin: 'TIN / Tax ID',
};

const Cart = () => {
    const navigate = useNavigate();
    const { postRequest } = useApiRequest();
    const { data, loading: productsLoading } = useFetch(`${domain}/products`);
    const { sessionId, cartItems, handleFetch, cartLoading } = useSessionId();
    const [total, setTotal] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [show, setShow] = useState(false);
    const [err, setErr] = useState(null);
    const [loadingProduct] = useState(false);

    const initial = {
        company: '',
        location: '',
        city: '',
        tin: '',
    };
    const [info, setInfo] = useState(initial);
    const { company, location, tin, city } = info;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInfo((prev) => ({ ...prev, [name]: value }));
    };

    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    const state = {
        products: data,
        cartItems,
        sessionId,
        info,
    };

    const handleQuotation = () => {
        for (const key of Object.keys(info)) {
            if (info[key] === '') {
                setErr(
                    `${FIELD_LABELS[key] || key} is required`
                );
                return;
            }
        }
        setErr(null);
        closeModal();
        navigate('/quotation', { state });
    };

    const handleClose = () => {
        setShow(false);
        setErr(null);
    };
    const handleShow = () => setShow(true);

    const removeFromCart = async (itemId) => {
        const formData = new FormData();
        formData.append('id', itemId);
        formData.append('session-id', sessionId);
        const res = await postRequest(`${domain}/remove-from-cart`, formData);
        if (res) handleFetch();
    };

    const addToCart = async (item) => {
        const formData = new FormData();
        formData.append('id', item.product);
        formData.append('session-id', sessionId);
        const res = await postRequest(`${domain}/add-to-cart`, formData);
        if (res) handleFetch();
    };

    const getProduct = useCallback(
        (productId) => {
            if (!data || loadingProduct) return { loading: true };
            const product = data.find((p) => p.id === parseInt(productId, 10));
            if (product) return { ...product, loading: false };
            return { loading: false, notFound: true };
        },
        [data, loadingProduct]
    );

    const getTotal = () => {
        let sum = 0;
        if (data && cartItems?.length) {
            cartItems.forEach((item) => {
                const found = data.find((p) => p.id === item.product);
                if (found) sum += found.selling_price * item.quantity;
            });
        }
        return sum;
    };

    useEffect(() => {
        setTotal(getTotal());
    }, [cartItems, data]);

    const itemCount = cartItems?.length ?? 0;
    const isEmpty = !cartLoading && itemCount === 0;

    return (
        <div className="cart-page">
            <Header />
            <main className="cart-main container py-4 py-md-5">
                <header
                    className="cart-page-header"
                    aria-labelledby="cart-page-title"
                >
                    <div className="cart-page-header-main">
                        <p className="cart-page-eyebrow">Your order</p>
                        <h1 id="cart-page-title" className="cart-page-title">
                            Shopping cart
                        </h1>
                        {!cartLoading && (
                            <p className="cart-page-meta">
                                {itemCount === 0 ? (
                                    'No items yet — add parts from the catalog.'
                                ) : (
                                    <>
                                        <span className="cart-page-count">
                                            {itemCount}
                                        </span>
                                        <span>
                                            line item{itemCount !== 1 ? 's' : ''}{' '}
                                            in this cart
                                        </span>
                                    </>
                                )}
                            </p>
                        )}
                    </div>
                    <Link to="/" className="cart-page-continue">
                        <i className="fa-solid fa-arrow-left-long me-2" aria-hidden />
                        Continue shopping
                    </Link>
                </header>

                {cartLoading && (
                    <div className="cart-loading card border-0 shadow-sm">
                        <div className="card-body text-center py-5">
                            <DNA
                                visible
                                height="64"
                                width="64"
                                ariaLabel="Loading cart"
                                wrapperClass="cart-loading-dna"
                            />
                            <p className="cart-loading-text mb-0 mt-3 text-secondary">
                                Loading your cart…
                            </p>
                        </div>
                    </div>
                )}

                {!cartLoading && productsLoading && itemCount > 0 && (
                    <p className="text-secondary small mb-3">
                        Loading product details…
                    </p>
                )}

                {isEmpty && (
                    <motion.div
                        className="cart-empty card border-0 shadow-sm"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                    >
                        <div className="card-body text-center py-5 px-4">
                            <div className="cart-empty-icon" aria-hidden>
                                <i className="fa-solid fa-cart-shopping" />
                            </div>
                            <h2 className="h5 mb-2">Your cart is empty</h2>
                            <p className="text-secondary mb-4 mx-auto cart-empty-copy">
                                Browse the catalog and add industrial parts. When
                                you&apos;re ready, you can generate a pro-forma
                                quotation from here.
                            </p>
                            <Link to="/" className="btn btn-primary cart-empty-cta">
                                Browse products
                            </Link>
                        </div>
                    </motion.div>
                )}

                {!cartLoading && !isEmpty && (
                    <div className="row g-4">
                        <div className="col-lg-8">
                            <ul className="cart-list list-unstyled mb-0">
                                {cartItems.map((item) => {
                                    const product = getProduct(item.product);
                                    return (
                                        <motion.li
                                            key={item.id}
                                            className="cart-line card border-0 shadow-sm mb-3"
                                            layout
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                        >
                                            <div className="card-body">
                                                {product.loading ? (
                                                    <div className="d-flex align-items-center gap-3 py-2">
                                                        <DNA
                                                            visible
                                                            height="48"
                                                            width="48"
                                                            ariaLabel="Loading"
 />
                                                        <span className="text-secondary small">
                                                            Loading product…
                                                        </span>
                                                    </div>
                                                ) : product.notFound ? (
                                                    <p className="text-danger mb-0 small">
                                                        Product not found (id:{' '}
                                                        {item.product})
                                                    </p>
                                                ) : (
                                                    <div className="cart-line-inner">
                                                        <div className="cart-line-media">
                                                            <img
                                                                src={
                                                                    domain +
                                                                    product.image
                                                                }
                                                                alt={
                                                                    product.product_name
                                                                }
                                                            />
                                                        </div>
                                                        <div className="cart-line-body">
                                                            <h2 className="cart-line-title">
                                                                <Link
                                                                    to={`/products-details/${product.id}`}
                                                                >
                                                                    {
                                                                        product.product_name
                                                                    }
                                                                </Link>
                                                                <span className="cart-line-part text-muted">
                                                                    {product.part_number}
                                                                </span>
                                                            </h2>
                                                            <p className="cart-line-condition mb-1">
                                                                <span className="text-muted">
                                                                    Condition:
                                                                </span>{' '}
                                                                {product.condition}
                                                            </p>
                                                            <p className="cart-line-price mb-0 small text-muted">
                                                                {product.selling_price.toLocaleString()}{' '}
                                                                UGX × {item.quantity}
                                                            </p>
                                                        </div>
                                                        <div className="cart-line-total">
                                                            <span className="cart-line-total-label">
                                                                Line total
                                                            </span>
                                                            <span className="cart-line-total-value">
                                                                {(
                                                                    product.selling_price *
                                                                    item.quantity
                                                                ).toLocaleString()}{' '}
                                                                <span className="cart-currency">
                                                                    UGX
                                                                </span>
                                                            </span>
                                                        </div>
                                                        <div className="cart-line-qty">
                                                            <span className="cart-line-qty-label">
                                                                Qty
                                                            </span>
                                                            <div className="cart-qty-controls">
                                                                <button
                                                                    type="button"
                                                                    className="cart-qty-btn cart-qty-btn--minus"
                                                                    onClick={() =>
                                                                        removeFromCart(
                                                                            item.product
                                                                        )
                                                                    }
                                                                    aria-label="Decrease quantity"
                                                                >
                                                                    <i className="fa-solid fa-minus" />
                                                                </button>
                                                                <span className="cart-qty-value">
                                                                    {item.quantity}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    className="cart-qty-btn cart-qty-btn--plus"
                                                                    onClick={() =>
                                                                        addToCart(item)
                                                                    }
                                                                    aria-label="Increase quantity"
                                                                >
                                                                    <i className="fa-solid fa-plus" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.li>
                                    );
                                })}
                            </ul>
                        </div>
                        <div className="col-lg-4">
                            <aside className="cart-summary card border-0 shadow-sm">
                                <div className="card-body">
                                    <h2 className="cart-summary-title">
                                        Order summary
                                    </h2>
                                    <p className="cart-summary-thanks text-muted small">
                                        Thank you for ordering with Industrial Parts.
                                    </p>
                                    <div className="cart-summary-row">
                                        <span>Subtotal</span>
                                        <span>
                                            {total.toLocaleString()}{' '}
                                            <span className="cart-currency">UGX</span>
                                        </span>
                                    </div>
                                    <p className="cart-summary-vat-note small text-muted mb-2">
                                        Prices exclude 18% VAT (Uganda).
                                    </p>
                                    <div className="cart-summary-row cart-summary-row--muted">
                                        <span>VAT (18%)</span>
                                        <span>
                                            {(total * 0.18).toLocaleString()}{' '}
                                            <span className="cart-currency">UGX</span>
                                        </span>
                                    </div>
                                    <div className="cart-summary-row cart-summary-total">
                                        <span>Total</span>
                                        <span>
                                            {(total * 1.18).toLocaleString()}{' '}
                                            <span className="cart-currency">UGX</span>
                                        </span>
                                    </div>
                                    <motion.button
                                        type="button"
                                        className="cart-summary-cta"
                                        onClick={handleShow}
                                        initial={{ y: 6 }}
                                        animate={{ y: 0 }}
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.98 }}
                                    >
                                        Generate quotation
                                    </motion.button>
                                    <p className="cart-summary-inquiry small text-muted mb-0 mt-3">
                                        Questions?{' '}
                                        <button
                                            type="button"
                                            className="cart-inquiry-link btn btn-link p-0 align-baseline"
                                            onClick={openModal}
                                        >
                                            Contact us
                                        </button>
                                        —we&apos;ll respond as soon as we can.
                                    </p>
                                </div>
                            </aside>
                        </div>
                    </div>
                )}

                <InquiryForm isOpen={isModalOpen} onClose={closeModal} />

                <Modal
                    show={show}
                    onHide={handleClose}
                    backdrop="static"
                    keyboard={false}
                    centered
                    className="cart-quotation-modal"
                >
                    <Modal.Header
                        closeButton
                        className="cart-modal-header border-0"
                    >
                        <Modal.Title className="h5 mb-0">
                            Quotation details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {err && (
                            <div className="alert alert-danger py-2 small" role="alert">
                                {err}
                            </div>
                        )}
                        <p className="text-secondary small mb-3">
                            These fields appear on your pro-forma invoice. All are
                            required.
                        </p>
                        <div className="mb-3">
                            <label htmlFor="cart-company" className="form-label small">
                                Company name
                            </label>
                            <input
                                id="cart-company"
                                type="text"
                                name="company"
                                value={company}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Registered business name"
                                autoComplete="organization"
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="cart-location" className="form-label small">
                                Address
                            </label>
                            <input
                                id="cart-location"
                                type="text"
                                name="location"
                                value={location}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="Plot, street, district"
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="cart-city" className="form-label small">
                                City
                            </label>
                            <input
                                id="cart-city"
                                type="text"
                                name="city"
                                value={city}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="e.g. Kampala"
                                autoComplete="address-level2"
                            />
                        </div>
                        <div className="mb-0">
                            <label htmlFor="cart-tin" className="form-label small">
                                TIN / Tax ID
                            </label>
                            <input
                                id="cart-tin"
                                type="text"
                                name="tin"
                                value={tin}
                                onChange={handleChange}
                                className="form-control"
                                placeholder="e.g. 1000034721"
                                inputMode="numeric"
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="outline-secondary" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleQuotation}>
                            Save &amp; view quotation
                        </Button>
                    </Modal.Footer>
                </Modal>
            </main>
            <Footer />
        </div>
    );
};

export default Cart;
